import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

// ✅ User एंटिटी के लिए एक टाइप परिभाषा जोड़ें (JSDoc के रूप में)
/**
 * @typedef {object} UserEntity
 * @property {string} id - यूजर की MongoDB ID
 * @property {string} _id - यूजर की MongoDB ID
 * @property {string} name
 * @property {string} email
 * @property {string} mobile
 * @property {object} [avatar]
 * @property {string} [avatar.public_id]
 * @string} [avatar.url]
 * @property {string[]} [platform]
 * @property {string} [loginType]
 * @property {string[]} role
 * @property {string} userStatus
 * @property {boolean} verified
 * @property {string[]} [refreshToken]
 * @property {string} [verifyExpire]
 * @property {number} [resetPasswordOtp]
 * @property {string} [resetPasswordOtpExpire]
 * @property {boolean} [hasJoinedCommunity]
 * @property {string} [joinedCommunityAt]
 * @property {string} [membershipTier] - NONE, BASIC, PREMIUM
 * @property {string} [membershipStatus] - ACTIVE, INACTIVE, PENDING, EXPIRED, CANCELLED, FREE_TRIAL, SPONSORED
 * @property {string} [membershipStartDate]
 * @property {string} [membershipEndDate]
 * @property {string} [lastPaymentDate]
 * @property {string} [currentTransactionId]
 * @property {boolean} [freeTrialUsed]
 * @property {boolean} [isSponsoredMember] // ✅ नया फ़ील्ड
 * @property {string} createdAt
 * @property {string} updatedAt
 */

const usersAdapter = createEntityAdapter({})

const initialState = usersAdapter.getInitialState()

export const usersApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUsers: builder.query({
            query: () => ({
                url: '/api/v1/users',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                // `UserEntity` टाइप में सभी नए फ़ील्ड्स को स्वचालित रूप से शामिल किया जाएगा
                // क्योंकि responseData में वे मौजूद होंगे
                const loadedUsers = responseData.map(user => {
                    user.id = user._id // `id` को `_id` से मैप करें
                    return user
                });
                return usersAdapter.setAll(initialState, loadedUsers)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'User', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'User', id }))
                    ]
                } else return [{ type: 'User', id: 'LIST' }]
            }
        }),
        addNewUser: builder.mutation({
            query: initialUserData => ({
                url: '/api/v1/users',
                method: 'POST',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'User', id: "LIST" }
            ]
        }),
        updateUser: builder.mutation({
            // ✅ `initialUserData` अब सभी संभव उपयोगकर्ता फ़ील्ड्स को स्वीकार करेगा
            // जिसमें सदस्यता फ़ील्ड्स और `isSponsoredMember` शामिल हैं
            query: initialUserData => ({
                url: '/api/v1/user/update', // सुनिश्चित करें कि यह आपके बैकएंड राउट से मेल खाता है
                method: 'PATCH',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'User', id: arg.id }
            ]
        }),
        deleteUser: builder.mutation({
            query: ({ id }) => ({
                url: `/api/v1/user/delete`, // सुनिश्चित करें कि यह आपके बैकएंड राउट से मेल खाता है
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'User', id: arg.id }
            ]
        }),
    }),
})

export const {
    useGetUsersQuery,
    useAddNewUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
} = usersApiSlice

// returns the query result object
export const selectUsersResult = usersApiSlice.endpoints.getUsers.select()

// creates memoized selector
const selectUsersData = createSelector(
    selectUsersResult,
    usersResult => usersResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllUsers,
    selectById: selectUserById,
    selectIds: selectUserIds
    // Pass in a selector that returns the users slice of state
} = usersAdapter.getSelectors(state => selectUsersData(state) ?? initialState)
